import { createContext, ReactNode, useCallback, useState } from 'react'
import * as Blockly from 'blockly/core'

import {
  commonResource,
  CommonResource,
  hiragana,
  kanji,
} from '../../../i18n/common'
import { LanguageTypes } from '../../../i18n/translation'

const DEFAULT_LANGUAGE = 'hiragana'
const setLanguage_ = (language: LanguageTypes) => {
  commonResource.language = language
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS is happy with this when building, but not in VSCode.
  Blockly.setLocale(language === 'hiragana' ? hiragana : kanji)
}
setLanguage_(DEFAULT_LANGUAGE)

interface TranslatorContextType {
  language: LanguageTypes
  setLanguage: (
    language: LanguageTypes,
    workspace?: Blockly.WorkspaceSvg,
  ) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: keyof CommonResource, substitutions?: Array<any>) => string
}

export const TranslatorContext = createContext<TranslatorContextType>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: () => '',
})

export const TranslatorProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<LanguageTypes>(DEFAULT_LANGUAGE)

  const setLanguageAndBlocklyLocale = useCallback(
    (language: LanguageTypes, workspace?: Blockly.WorkspaceSvg) => {
      setLanguage(language)
      setLanguage_(language)
      if (workspace) {
        // Save and load the workspace to update the block texts.
        const state = Blockly.serialization.workspaces.save(workspace)
        Blockly.serialization.workspaces.load(state, workspace)

        // Update the toolbox category labels.
        const toolbox = workspace.getToolbox() as Blockly.Toolbox
        toolbox?.getToolboxItems().forEach((category) => {
          const cat = category as Blockly.ToolboxCategory
          const elem = document.getElementById(cat.getId() + '.label')
          const key = (
            cat.toolboxItemDef_ as Blockly.utils.toolbox.StaticCategoryInfo
          )?.name?.match(/C_\w+/)?.[0]
          if (elem && key) {
            const s = commonResource.to(key as keyof CommonResource)
            if (s) elem.textContent = s
          }
        })

        // Update the flyout contents.
        workspace.refreshToolboxSelection()
      }
    },
    [],
  )

  const t = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: keyof CommonResource, substitutions?: Array<any>) =>
      commonResource.to(key, substitutions),
    // We want this function to update when the language changes.
    // This will trigger re-rendering of components that use translation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language],
  )

  return (
    <TranslatorContext.Provider
      value={{ language, setLanguage: setLanguageAndBlocklyLocale, t }}
    >
      {children}
    </TranslatorContext.Provider>
  )
}
